import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useWatch } from 'react-hook-form';
import { calculate, type CalculateMaxMortgageProps } from '~/api/calculations';
import {
  MortgagePeriodMap,
  type LoadedMortgageCalculationEntry,
  type MortgagePeriod,
  type MortgageType,
  type ProviderMortgageResult,
} from '~/types/Calculations';
import type { ProviderMatch } from '~/types/Provider';
import { useCalculateMaximumMortgageForm } from './FormProvider';

type CalculationContextValues = {
  isCalculating: boolean;
  loadedState: LoadedMortgageCalculationEntry[];
  isError: boolean | null;
  results: ProviderMortgageResult[];
  providerMatches: ProviderMatch[];
  selected?: ProviderMatch;
  handleCalculation: (data: CalculateMaxMortgageProps) => void;
  handleSelect: (slug: string) => void;
  hasCalculated: boolean;
  setHasCalculated: React.Dispatch<React.SetStateAction<boolean>>;
  defaultPeriod: MortgagePeriod;
  periods: MortgagePeriod[];
  defaultType: MortgageType;
  types: MortgageType[];
  nhgEnabled: boolean;
  energyLabelsEnabled: boolean;
};

const CalculationContext = createContext<CalculationContextValues | undefined>(
  undefined
);

type CalculateMaximumMortgageCalculationProviderProps = {
  officeId?: string;
  children: React.ReactNode;
  defaultIncome?: number;
  defaultAge?: number;
  defaultPeriod: MortgagePeriod;
  periods: MortgagePeriod[];
  defaultType: MortgageType;
  types: MortgageType[];
  nhgEnabled: boolean;
  energyLabelsEnabled: boolean;
};

export const CalculateMaximumMortgageCalculationProvider: React.FC<
  CalculateMaximumMortgageCalculationProviderProps
> = ({
  children,
  defaultIncome,
  defaultAge,
  officeId,
  defaultPeriod,
  defaultType,
  types,
  periods,
  nhgEnabled,
  energyLabelsEnabled,
}) => {
  const { control, resetField, getValues } = useCalculateMaximumMortgageForm();

  const mortgageType = useWatch({
    control,
    name: 'mortgageType',
    defaultValue: defaultType,
  });

  const interestPeriod = useWatch({
    control,
    name: 'interestPeriod',
    defaultValue: MortgagePeriodMap[defaultPeriod],
  });

  const useNhg = useWatch({ control, name: 'useNhg', defaultValue: false });

  const energyLabel = useWatch({
    control,
    name: 'energyLabel',
    defaultValue: 'Unknown',
  });

  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [loadedState, setLoadedState] = useState<
    LoadedMortgageCalculationEntry[]
  >([]);
  const [isError, setIsError] = useState<boolean | null>(null);

  const [results, setResults] = useState<ProviderMortgageResult[]>([]);
  const [providerMatches, setProviderMatches] = useState<ProviderMatch[]>([]);
  const [selected, setSelected] = useState<ProviderMatch | undefined>(
    undefined
  );

  const [hasCalculated, setHasCalculated] = useState<boolean>(false);

  const controllerRef = useRef<AbortController | null>(null);

  const mergeResults = (
    current: ProviderMortgageResult[],
    updated: ProviderMortgageResult[]
  ): ProviderMortgageResult[] => {
    const map = new Map<string, ProviderMortgageResult>();
    current.forEach((p) => map.set(p.slug, p));

    updated.forEach((p) => {
      let provider = p;
      if (map.has(p.slug)) {
        const existing = map.get(p.slug)!;
        provider.mortgages = [
          ...(existing.mortgages ?? []),
          ...(p.mortgages ?? []),
        ];
      }

      map.set(p.slug, provider);
    });

    return Array.from(map.values());
  };

  const mergeLoadState = (
    current: LoadedMortgageCalculationEntry[],
    updated: LoadedMortgageCalculationEntry[]
  ): LoadedMortgageCalculationEntry[] => {
    const map = new Map<MortgageType, LoadedMortgageCalculationEntry>();
    current.forEach((c) => map.set(`${c.type}`, c));

    updated.forEach((e) => {
      let entry = e;
      if (map.has(entry.type)) {
        const existing = map.get(entry.type)!;
        entry.periods = [...(existing.periods ?? []), ...(entry.periods ?? [])];
      }

      map.set(entry.type, entry);
    });

    return Array.from(map.values());
  };

  const handleCalculation = (data: CalculateMaxMortgageProps) => {
    controllerRef.current?.abort();

    resetField('mortgageType');
    resetField('interestPeriod');

    setIsCalculating(true);
    setHasCalculated(false);
    setLoadedState([]);
    setResults([]);
    setIsError(false);

    calculate(
      data,
      undefined,
      defaultType,
      [defaultPeriod],
      officeId,
      nhgEnabled,
      energyLabelsEnabled
    )
      .then((response) => {
        setResults((previous) => mergeResults(previous, response.results));
        setLoadedState((current) =>
          mergeLoadState(current, response.metadata.loadedEntries)
        );
        handleRemainingCalculations(data);
      })
      .catch(() => setIsError(true))
      .finally(() => {
        setIsCalculating(false);
        setHasCalculated(true);
      });
  };

  const doRemainingCalculation = (
    data: CalculateMaxMortgageProps,
    type: MortgageType,
    signal: AbortSignal
  ) => {
    calculate(
      data,
      signal,
      type,
      [],
      officeId,
      nhgEnabled,
      energyLabelsEnabled
    )
      .then((response) => {
        setResults((current) => mergeResults(current, response.results));
        setLoadedState((current) =>
          mergeLoadState(current, response.metadata.loadedEntries)
        );
      })
      .catch((err) => {
        if (err.name != 'AbortError') {
          setIsError(true);
        }
      });
  };

  const handleRemainingCalculations = async (
    data: CalculateMaxMortgageProps
  ) => {
    const controller = new AbortController();
    controllerRef.current = controller;

    types.forEach((type) => {
      doRemainingCalculation(data, type, controller.signal);
    });
  };

  useEffect(() => {
    let cancelled = false;

    if (defaultIncome && defaultAge) {
      setIsCalculating(true);
      setHasCalculated(false);
      setIsError(null);

      const data = {
        income: defaultIncome,
        age: defaultAge,
        hasPartner: false,
      };

      calculate(
        data,
        undefined,
        defaultType,
        [defaultPeriod],
        officeId,
        nhgEnabled,
        energyLabelsEnabled
      )
        .then((response) => {
          if (!cancelled) {
            setResults(response.results);
            handleRemainingCalculations(data);
            setLoadedState((current) =>
              mergeLoadState(current, response.metadata.loadedEntries)
            );
          }
        })
        .catch(() => {
          if (!cancelled) {
            setIsError(true);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsCalculating(false);
            setHasCalculated(true);
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [defaultIncome, defaultAge]);

  useEffect(() => {
    if (results.length > 0) {
      const providers: ProviderMatch[] = [];
      const periodInMonths = parseInt(interestPeriod) * 12;

      results.forEach((result) => {
        const mortgage = result.mortgages?.find(
          (m) =>
            m.type.toLowerCase() === mortgageType.toLowerCase() &&
            m.fixedRatePeriodInMonths === periodInMonths
        );

        if (mortgage) {
          providers.push({
            slug: result.slug,
            name: result.name,
            logoUrl: result.logoUrl,
            sortOrder: result.sortOrder,
            campaignCode: result.campaignCode,
            mortgage: mortgage,
          });
        }
      });

      providers.sort(
        (a, b) =>
          a.mortgage.grossMonthlyCosts - b.mortgage.grossMonthlyCosts ||
          a.mortgage.rate - b.mortgage.rate
      );

      setProviderMatches(providers);

      if (providers.length > 0) {
        setSelected(providers[0]);
      }
    }
  }, [mortgageType, interestPeriod, results]);

  const skipNextNhgEffect = useRef(true);

  useEffect(() => {
    if (skipNextNhgEffect.current) {
      skipNextNhgEffect.current = false;
      return;
    }

    if (!hasCalculated) return;

    handleCalculation(getValues());
  }, [useNhg, energyLabel]);

  const handleSelect = (slug: string) => {
    const match =
      providerMatches.find((p) => p.slug === slug) ?? providerMatches[0];
    setSelected(match);
  };

  return (
    <CalculationContext.Provider
      value={{
        isCalculating,
        loadedState,
        isError,
        results,
        providerMatches,
        selected,
        handleCalculation,
        handleSelect,
        hasCalculated,
        setHasCalculated,
        periods,
        types,
        defaultPeriod,
        defaultType,
        nhgEnabled,
        energyLabelsEnabled,
      }}
    >
      {children}
    </CalculationContext.Provider>
  );
};

export const useCalculateMaximumMortgageCalculation = () => {
  const context = useContext(CalculationContext);
  if (!context) {
    throw new Error(
      'useCalculateMaximumMortgageCalculationProvider must be used within a CalculateMaximumMortgageCalculationProvider'
    );
  }
  return context;
};
